/*
  Support hero — pointer parallax.

  THIS FILE IS ENTIRELY OPTIONAL. It adds a few pixels of drift to decorative
  symbols that already move on their own in CSS. If it never runs, never loads,
  or throws, the hero is exactly what the stylesheet already made it: nothing on
  this page depends on it, and it renders no content.

  IT ONLY RUNS WHERE THE GESTURE EXISTS. A parallax driven by pointer position
  is meaningless on a touch screen — there is no hovering pointer to follow — so
  it is gated on a real fine pointer that can hover. It is also gated on the
  reduced-motion preference, which is checked live rather than once, so a person
  turning the preference on mid-session gets it honoured without reloading.

  IT READS LAYOUT ONCE PER FRAME, NOT PER EVENT. The pointer handler only stores
  two numbers; all measurement and all writing happen inside one animation
  frame. A pointermove handler that read geometry would run layout dozens of
  times a second for a decorative effect.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-gd-support-hero]';
  var MAX = 5; // pixels — the cap the effect was specified at
  var instances = [];

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  function Parallax(root) {
    this.root = root;
    this.symbols = Array.prototype.slice.call(root.querySelectorAll('.gd-support-decor'));
    this.targetX = 0;
    this.targetY = 0;
    this.frame = null;
    this.onMove = this.onMove.bind(this);
    this.render = this.render.bind(this);

    if (this.symbols.length) {
      root.addEventListener('pointermove', this.onMove);
      root.addEventListener('pointerleave', this.onMove);
    }
  }

  /*
    The handler is deliberately dumb: it stores the pointer position and asks
    for a frame. Nothing is measured and nothing is written here.
  */
  Parallax.prototype.onMove = function (event) {
    if (event.type === 'pointerleave') {
      this.targetX = 0;
      this.targetY = 0;
    } else {
      var rect = this.root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.targetX = (event.clientX - rect.left) / rect.width - 0.5;
      this.targetY = (event.clientY - rect.top) / rect.height - 0.5;
    }

    if (this.frame === null) {
      this.frame = window.requestAnimationFrame(this.render);
    }
  };

  Parallax.prototype.render = function () {
    this.frame = null;

    // Checked at write time, so the preference can change mid-session.
    if (motionQuery.matches) {
      this.reset();
      return;
    }

    for (var i = 0; i < this.symbols.length; i++) {
      // Alternating depth, so the set does not slide as one plane.
      var depth = i % 2 === 0 ? 1 : -0.6;
      var x = (this.targetX * MAX * depth).toFixed(2);
      var y = (this.targetY * MAX * depth).toFixed(2);
      this.symbols[i].style.setProperty('--gd-sup-px', x + 'px');
      this.symbols[i].style.setProperty('--gd-sup-py', y + 'px');
    }
  };

  Parallax.prototype.reset = function () {
    for (var i = 0; i < this.symbols.length; i++) {
      this.symbols[i].style.removeProperty('--gd-sup-px');
      this.symbols[i].style.removeProperty('--gd-sup-py');
    }
  };

  Parallax.prototype.destroy = function () {
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerleave', this.onMove);
    if (this.frame !== null) window.cancelAnimationFrame(this.frame);
    this.reset();
  };

  /*
    A SECTION CAN BE REBUILT UNDER US IN THE THEME EDITOR.

    Editing a setting replaces the section's DOM without a page load. Binding
    again without tearing down the previous instance would leave a listener
    attached to a detached node, and every edit would add another. Teardown is
    unconditional and runs before every bind, including the first.
  */
  function teardown() {
    while (instances.length) instances.pop().destroy();
  }

  function bind() {
    teardown();
    if (!pointerQuery.matches) return;

    var roots = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      instances.push(new Parallax(roots[i]));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  // A pointer can change: a tablet gaining a mouse, a window moving screens.
  if (typeof pointerQuery.addEventListener === 'function') {
    pointerQuery.addEventListener('change', bind);
  }

  document.addEventListener('shopify:section:load', bind);
  document.addEventListener('shopify:section:unload', teardown);
})();
